import moment from 'moment'
import { initializeApp } from "firebase/app"
import { 
  getDatabase, ref, set, 
  update, remove, onValue 
} from "firebase/database"
import {
  getAuth,
  signOut,
} from "firebase/auth"

import ApiService from './ApiService'

class FirebaseService {
  constructor (protocol, pacNome) {
    console.log('CONSTRUCT FirebaseService')
    this.fbDatabase = null
    this.path = `users/$cpf/telessaude/sala/$cpf`
    this.pathLog = `telessaude-log/$cpf/`
    this.setFirebaseConfig()
    this.ApiService = new ApiService()

    this.protocol = protocol || null
    this.pacNome = pacNome || null
  }

  /**
   * Configura a conexão com o Firebase.
   */
  setFirebaseConfig () {
    const config = {
      apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
      authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
      databaseURL: process.env.VUE_APP_FIREBASE_DATABASE_URL,
      projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
      storageBucket: process.env.VUE_APP_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: process.env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID,
      appId: process.env.VUE_APP_FIREBASE_APP_ID
    }
    const firebaseApp = initializeApp(config)
    this.fbDatabase = getDatabase(firebaseApp)
    this.fbAuth = getAuth(firebaseApp)
  }

  /**
   * Define o valor da sala no Firebase Realtime Database.
   * @param {string} cpf - O CPF do usuário.
   * @param {object} agoraSdk - Informações adicionais para o payload.
   */
  async setFirebaseRoom (cpf, agoraSdk, callback) {
    const cpfFormat = cpf.replace(/[.-]/g, '')
    const payload = {
      protocol: this.protocol || null,
      pacNome: this.pacNome || null,
      cpf: cpfFormat,
      agoraSdk,
      funNome: null,
      funStreamId: null,
      pacStreamId: null,
      created_at: moment().format('DD/MM/YYYY - HH:mm:ss')
    }
    const path = this.path.replace(/\$cpf/g, cpfFormat)
    set(ref(this.fbDatabase, path), payload)
    .then(async () => {
      console.info('[setFirebaseRoom] sala criada/atualizada com sucesso')
      const payloadLog = {
        type: 'INSERT',
        origem: 'Portal Paciente SUS',
        protocol: this.protocol,
        pacNome: this.pacNome,
        cpf: cpfFormat || 'Não informado',
        payload: JSON.stringify(payload),
        createdAt: moment().format('YYYY-MM-DD HH:mm:ss'),
        subtype: `Sala criada: ${agoraSdk.canal}`
      }
      await this.ApiService.saveTeleconsultMovimentLog(payloadLog)
      
      const payloadRoom = {
        tsa_protocolo: this.protocol,
        tsa_cpf: cpfFormat,
        tsa_sala: agoraSdk.canal,
        tsa_tempo_espera: '00:00',
        tsa_duracao_consulta: '00:00',
        tsa_paciente_nome: this.pacNome || null,
        tsa_profissional_nome: null,
      }
      const responseSaveRoom = await this.ApiService.saveTeleconsultRoom(payloadRoom)
      callback(responseSaveRoom)
      
      return {
        status: true,
        ...responseSaveRoom
      }
    })
    .catch((error) => {
      console.error('[setFirebaseRoom] Erro:', error)
      return {
        status: false
      }
    })
  }

  /**
   * Atualiza uma chave específica na sala do Firebase com base no CPF fornecido.
   * @param {string} cpf - O CPF do usuário, contendo pontos e traços (que serão removidos).
   * @param {string} key - A chave específica a ser atualizada no Firebase.
   * @param {*} value - O valor a ser aplicado à chave.
   * @returns {Promise<boolean>} Retorna `true` se a atualização for bem-sucedida, caso contrário, `false`.
  */
  async updateFirebaseRoom (cpf, key, value, saveMovementLog = true) {
    const cpfFormat = cpf.replace(/[.-]/g, '')
    const path = `${this.path.replace(/\$cpf/g, cpfFormat)}/agoraSdk`
    const payload = {
      [key]: value,
      updated_at: moment().format('DD/MM/YYYY - HH:mm:ss') // Atualiza também a data de modificação
    }  
    update(ref(this.fbDatabase, path), payload)
    .then(async () => {
      // console.info(`[updateFirebaseRoom] Chave ${key} atualizada com sucesso`)
      const payloadLog = {
        type: 'UPDATE',
        origem: 'Portal Paciente SUS',
        protocol: this.protocol,
        pacNome: this.pacNome,
        cpf: cpfFormat || 'Não informado',
        payload: JSON.stringify(payload),
        createdAt: moment().format('YYYY-MM-DD HH:mm:ss'),
        subtype: `${key}: ${value}`
      }
      if (saveMovementLog) {
        if (key === 'funStreamId' && value !== null) {
          payloadLog.subtype = `Profissional entrou na sala`
        } else {
          payloadLog.subtype = `Profissional saiu da sala`
        }

        if (key === 'funNome') {
          return
        }
        
        if (key === 'pacStreamId' && value !== null) {
          payloadLog.subtype = `Paciente entrou na sala`
        } else {
          payloadLog.subtype = `Paciente saiu da sala`
        }

        if (key === 'pergunta') {
          payloadLog.subtype = `Respondendo pergunta`
        }
        await this.ApiService.saveTeleconsultMovimentLog(payloadLog)
      }
      return true
    })
    .catch((error) => {
      console.error(`[updateFirebaseRoom] Erro ao atualizar a chave ${key}:`, error)
      return false
    })
  }

  /**
   * Remove a sala de telessaúde de um paciente com base no CPF.
   * @param {string} cpf - O CPF do paciente (chave do Firebase).
   * @returns {Promise<void>} - Retorna uma promessa que resolve quando a sala for removida.
   */
  async removeTelehealthRoom (cpf) {
    const cpfFormat = cpf.replace(/[.-]/g, '')
    const path = `users/${cpfFormat}/telessaude/sala`
    const roomRef = ref(this.fbDatabase, path)
    remove(roomRef)
      .then(async () => {
        const payloadLog = {
          type: 'DELETE',
          origem: 'Portal Paciente SUS',
          protocol: this.protocol,
          pacNome: this.pacNome,
          cpf: cpfFormat || 'Não informado',
          payload: '',
          createdAt: moment().format('YYYY-MM-DD HH:mm:ss'),
          subtype: `Sala destruída pelo Paciente`
        }
        await this.ApiService.saveTeleconsultMovimentLog(payloadLog)
      })
      .catch((error) => {
        console.error('Erro ao remover a sala de telessaúde:', error)
      })
  }

  /**
   * Escuta as mudanças na sala associada ao CPF no Firebase.
   * @param {string} cpf - O CPF do usuário.
   * @param {function} callback - Função de callback que será chamada quando ocorrerem mudanças nos dados da sala.
   */
  listenerRoomByCpf(cpf, callback) {
    const cpfFormat = cpf.replace(/[.-]/g, '');
    const path = this.path.replace(/\$cpf/g, cpfFormat);
    const roomRef = ref(this.fbDatabase, path);

    onValue(roomRef, (snapshot) => {
      const currentData = snapshot.val();
      callback(currentData)
    }, {
      onlyOnce: false
    });
  }
  async logout() {
    await signOut(this.fbAuth);
  }
}

export default FirebaseService